<template>
    <div class="">
        <div class="ppv2-p-6">
            <div>
                <Header :title="this.$t('sidebar.exports')" />
                <!-- Export Cards Grid -->
                <div class="ppv2-grid ppv2-grid-cols-1 lg:ppv2-grid-cols-2 ppv2-gap-8 ppv2-mb-10">
                    <!-- Documents Comptables Card -->
                    <ExportCard
                        v-if="canDownloadDocumentsComptables"
                        :title="$t('exports_v3.comptable_title')"
                        :description="$t('exports_v3.comptable_description')"
                        :buttonLabel="$t('exports_v3.generate')"
                        :loading="isGeneratingComptable"
                        @submit="handleComptableExport"
                    >
                        <!-- Date Range -->
                        <div class="ppv2-mb-6">
                            <BaseLabel
                                customClass="poppinsMedium ppv2-mb-3"
                                :customSizeLabel="14"
                                :label="$t('exports_v3.date_range_label')"
                                :required="true"
                            />
                            <invoice-date-filter
                                :start-date.sync="comptable.startDate"
                                :end-date.sync="comptable.endDate"
                                :max-date="comptableMaxDate"
                            />
                        </div>

                        <!-- Document Type Selection -->
                        <div class="ppv2-mb-2">
                            <BaseLabel
                                customClass="poppinsMedium ppv2-mb-3"
                                :customSizeLabel="14"
                                :label="$t('exports_v3.document_label')"
                                :required="true"
                            />
                            <div class="ppv2-grid ppv2-grid-cols-2 ppv2-gap-y-4 ppv2-gap-x-4">
                                <label
                                    v-for="option in comptableOptions"
                                    :key="option.value"
                                    class="ppv2-flex ppv2-items-center ppv2-cursor-pointer ppv2-group"
                                >
                                    <input
                                        v-model="comptable.documentType"
                                        type="radio"
                                        :value="option.value"
                                        @change="handleDocumentTypeChange"
                                        class="ppv2-mr-3 ppv2-h-5 ppv2-w-5 ppv2-accent-[#7D2DBD] ppv2-cursor-pointer"
                                    />
                                    <span
                                        class="poppinsRegular ppv2-text-black group-hover:ppv2-text-[#7D2DBD] ppv2-transition-colors"
                                        :style="{ fontSize: normalizeFontSize(14) }"
                                        >{{ option.label }}</span
                                    >
                                </label>
                            </div>
                        </div>
                    </ExportCard>

                    <!-- Rapports Opérationnels Card -->
                    <ExportCard
                        v-if="canDownloadDocumentsOperationelle"
                        :title="$t('exports_v3.operationnel_title')"
                        :description="$t('exports_v3.operationnel_description')"
                        :buttonLabel="$t('exports_v3.generate')"
                        :loading="isGeneratingOperationnel"
                        @submit="handleOperationnelExport"
                    >
                        <!-- Le Ticket X porte toujours sur la session en cours (depuis la dernière
                             clôture) : pas de période à choisir, elle serait ignorée. -->
                        <div class="ppv2-mb-6" v-if="!isTicketX">
                            <BaseLabel
                                customClass="poppinsMedium ppv2-mb-3"
                                :customSizeLabel="14"
                                :label="$t('exports_v3.date_range_label')"
                                :required="true"
                            />
                            <invoice-date-filter
                                :start-date.sync="operationnel.startDate"
                                :end-date.sync="operationnel.endDate"
                            />
                        </div>
                        <p
                            v-else
                            class="poppinsRegular ppv2-mb-6 ppv2-text-gray-500"
                            :style="{ fontSize: normalizeFontSize(13) }"
                        >
                            {{ $t('exports_v3.ticket_x_period_hint') }}
                        </p>
                        <!-- Rapport Type Selection -->
                        <div class="ppv2-mb-2">
                            <BaseLabel
                                customClass="poppinsMedium ppv2-mb-3"
                                :customSizeLabel="14"
                                :label="$t('exports_v3.report_label')"
                                :required="true"
                            />
                            <div class="ppv2-grid ppv2-grid-cols-2 ppv2-gap-y-4 ppv2-gap-x-4">
                                <label
                                    v-for="option in operationnelOptions"
                                    :key="option.value"
                                    class="ppv2-flex ppv2-items-center ppv2-cursor-pointer ppv2-group"
                                >
                                    <input
                                        v-model="operationnel.reportType"
                                        type="radio"
                                        :value="option.value"
                                        class="ppv2-mr-3 ppv2-h-5 ppv2-w-5 ppv2-accent-[#7D2DBD] ppv2-cursor-pointer"
                                    />
                                    <span
                                        class="poppinsRegular ppv2-text-black group-hover:ppv2-text-[#7D2DBD] ppv2-transition-colors"
                                        :style="{ fontSize: normalizeFontSize(14) }"
                                        >{{ option.label }}</span
                                    >
                                </label>
                            </div>
                        </div>
                    </ExportCard>
                </div>

                <!-- Export History Section -->
                <div>
                    <Header :title="$t('exports_v3.history_title')" :customFontSize="24" />

                    <!-- DataTable -->
                    <DataTable
                        :columns="tableColumns"
                        :items="exportHistory"
                        :enableMultiSelect="false"
                        :enableDragDrop="false"
                        :currentPage="currentPage"
                        :totalPages="totalPages"
                        :perPage="perPage"
                        @page-changed="handlePageChange"
                        @per-page-changed="handlePerPageChange"
                    >
                        <!-- Column: name (file) -->
                        <template #column-name="{ item }">
                            <a
                                v-if="item.status !== 'processing'"
                                href="#"
                                @click.prevent="downloadExport(item)"
                                class="poppinsRegular ppv2-text-black ppv2-underline ppv2-cursor-pointer hover:ppv2-text-[#7D2DBD] ppv2-transition-colors"
                            >
                                {{ item.name }}
                            </a>
                            <span v-else class="poppinsRegular ppv2-text-black">{{ item.name }}</span>
                        </template>

                        <!-- Column: generated_at -->
                        <template #column-generated_at="{ item }">
                            <div
                                v-if="item.status === 'processing'"
                                class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-text-[#7D2DBD]"
                            >
                                <RefreshCw class="ppv2-animate-spin ppv2-w-4 ppv2-h-4" />
                                <span class="poppinsRegular">{{ item.generated_at }}</span>
                            </div>
                            <span v-else class="poppinsRegular ppv2-text-black">{{ item.generated_at }}</span>
                        </template>

                        <!-- Column: generated_by -->
                        <template #column-generated_by="{ item }">
                            <span class="poppinsRegular ppv2-text-black ppv2-underline ppv2-cursor-pointer">
                                {{ item.generated_by }}
                            </span>
                        </template>

                        <!-- Column: actions -->
                        <template #column-actions="{ item }">
                            <div class="ppv2-flex ppv2-justify-center">
                                <button
                                    v-if="item.status !== 'processing'"
                                    class="ppv2-text-black hover:ppv2-text-[#7D2DBD] ppv2-transition-colors"
                                    @click="downloadExport(item)"
                                >
                                    <Download class="ppv2-w-5 ppv2-h-5" />
                                </button>
                            </div>
                        </template>
                    </DataTable>
                </div>
            </div>
        </div>

        <!-- "Période non disponible" popup (period crossing 1 June 2026) -->
        <CustomDialog v-model="showPeriodPopup" :default-title="$t('exports_v3.period_popup.title')" width="480px">
            <template #content>
                <p
                    class="poppinsRegular ppv2-text-black ppv2-text-center"
                    :style="{ fontSize: normalizeFontSize(14), lineHeight: '1.6' }"
                >
                    {{ $t('exports_v3.period_popup.message') }}
                </p>
            </template>
            <template #actions>
                <BaseButton
                    type="button"
                    variant="primary"
                    @click="showPeriodPopup = false"
                    customClass="!ppv2-min-h-[40px] !ppv2-h-[45px] !ppv2-min-w-[135px]"
                    :customHeight="43"
                >
                    {{ $t('exports_v3.period_popup.button') }}
                </BaseButton>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import axios from 'axios'
import normalizeSize from '../../../utils/normalizeSize'
import DataTable from '../../Shared/DataTable.vue'
import BaseLabel from '../../Shared/BaseLabel.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import ExportCard from './ExportCard.vue'
import { Download, RefreshCw } from 'lucide-vue'
import Header from '../../Shared/Header.vue'
import normalizeFontSize from '../../../utils/normalizeFontSize'
import CustomDialog from '../../Shared/CustomDialog.vue'
import { getPerPage, setPerPage } from '../../../utils/perPage'

export default {
    name: 'ExportV3Index',
    components: {
        DataTable,
        BaseButton,
        BaseLabel,
        ExportCard,
        Download,
        RefreshCw,
        Header,
        CustomDialog,
    },
    props: {
        userId: {
            type: [Number, String, null],
            default: null,
        },
        apiToken: {
            type: String,
            default: null,
        },
        canDownloadDocumentsComptables: {
            type: Boolean,
            default: false,
        },
        canDownloadDocumentsOperationelle: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        const today = new Date()
        const todayStr = today.toISOString().split('T')[0]

        const yesterday = new Date(today)
        yesterday.setDate(yesterday.getDate() - 1)
        const yesterdayStr = yesterday.toISOString().split('T')[0]

        return {
            comptable: {
                startDate: yesterdayStr,
                endDate: yesterdayStr,
                documentType: 'ticket_z_pdf',
            },
            operationnel: {
                startDate: todayStr,
                endDate: todayStr,
                reportType: 'ticket_x_encaissements',
            },
            comptableOptions: [
                { value: 'ticket_z_pdf', label: this.$t('exports_v3.options.ticket_z_pdf') },
                { value: 'fec', label: this.$t('exports_v3.options.fec') },
                { value: 'ticket_z_excel', label: this.$t('exports_v3.options.ticket_z_excel') },
                { value: 'factures_pdf', label: this.$t('exports_v3.options.factures_pdf') },
            ],
            operationnelOptions: [
                { value: 'ticket_x_encaissements', label: this.$t('exports_v3.options.ticket_x_encaissements') },
                { value: 'ticket_x_ventes', label: this.$t('exports_v3.options.ticket_x_ventes') },
                { value: 'ticket_x_encaissements_excel', label: this.$t('exports_v3.options.ticket_x_encaissements_excel') },
                { value: 'ticket_x_ventes_excel', label: this.$t('exports_v3.options.ticket_x_ventes_excel') },
                { value: 'mes_commandes', label: this.$t('exports_v3.options.mes_commandes') },
                { value: 'reservations', label: this.$t('exports_v3.options.reservations') },
            ],
            isGeneratingComptable: false,
            isGeneratingOperationnel: false,
            currentPage: 1,
            totalPages: 1,
            perPage: getPerPage('exports_items_per_page', 10),
            tableColumns: [
                { key: 'name', label: this.$t('exports_v3.columns.file'), sortable: true, type: 'text' },
                { key: 'period', label: this.$t('exports_v3.columns.period'), sortable: true, type: 'text' },
                { key: 'generated_at', label: this.$t('exports_v3.columns.generated_at'), sortable: true, type: 'date' },
                { key: 'size', label: this.$t('exports_v3.columns.size'), sortable: true, type: 'number' },
                { key: 'generated_by', label: this.$t('exports_v3.columns.generated_by'), sortable: true, type: 'text' },
                { key: 'actions', label: this.$t('exports_v3.columns.action'), sortable: false, type: 'actions' },
            ],
            exportHistory: [],
            isLoadingHistory: false,
            // "Période non disponible" popup, shown when the selected period
            // straddles the export cut-off date (1 June 2026).
            showPeriodPopup: false,
        }
    },
    computed: {
        comptableMaxDate() {
            if (this.comptable.documentType && this.comptable.documentType.startsWith('ticket_z')) {
                const today = new Date()
                const yesterday = new Date(today)
                yesterday.setDate(yesterday.getDate() - 1)
                return yesterday.toISOString().split('T')[0]
            }
            return false
        },
        isTicketX() {
            return this.operationnel.reportType.startsWith('ticket_x_')
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,

        authHeaders() {
            return this.apiToken ? { Authorization: `Bearer ${this.apiToken}` } : {}
        },

        // Export requests use responseType 'blob', so error bodies come back as a
        // Blob: read and JSON-parse it to inspect the backend payload (e.g. the
        // PERIOD_NOT_AVAILABLE code returned when the period crosses 1 June 2026).
        async parseErrorPayload(error) {
            const data = error?.response?.data
            if (!data) return null
            try {
                if (data instanceof Blob) {
                    return JSON.parse(await data.text())
                }
                if (typeof data === 'string') {
                    return JSON.parse(data)
                }
                return data
            } catch (e) {
                return null
            }
        },

        // Returns true (and opens the popup) when the backend signalled that the
        // selected period straddles the export cut-off date.
        async handlePeriodNotAvailable(error) {
            const payload = await this.parseErrorPayload(error)
            if (payload && payload.code === 'PERIOD_NOT_AVAILABLE') {
                this.showPeriodPopup = true
                return true
            }
            return false
        },

        async fetchHistory(page = 1) {
            this.isLoadingHistory = true
            try {
                const response = await axios.get('/api/v3/export/history', {
                    params: { page, per_page: this.perPage, user_id: this.userId },
                    headers: this.authHeaders(),
                })
                this.exportHistory = response.data.data
                this.currentPage = response.data.current_page
                this.totalPages = response.data.last_page
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.isLoadingHistory = false
            }
        },

        handleDocumentTypeChange() {
            if (this.comptable.documentType.startsWith('ticket_z')) {
                // Determine today's date in string format
                const todayStr = new Date().toISOString().split('T')[0]

                // Determine max date (yesterday string format)
                const today = new Date()
                const yesterday = new Date(today)
                yesterday.setDate(yesterday.getDate() - 1)
                const maxDateStr = yesterday.toISOString().split('T')[0]

                // If current start or end date is greater than yesterday, snap to yesterday
                if (this.comptable.startDate > maxDateStr) {
                    this.comptable.startDate = maxDateStr
                }
                if (this.comptable.endDate > maxDateStr) {
                    this.comptable.endDate = maxDateStr
                }
            }
        },

        async handleComptableExport() {
            if (!this.comptable.startDate || !this.comptable.endDate) {
                this.$toast?.error?.(this.$t('exports_v3.messages.select_dates_error'))
                return
            }

            this.isGeneratingComptable = true

            try {
                const endpoint = this.getEndpoint()
                const payload = this.getPayload()

                const response = await axios.post(endpoint, payload, {
                    responseType: 'blob',
                    headers: this.authHeaders(),
                })

                const fileName = this.getFileName()
                this.downloadFile(response.data, fileName)

                this.$toast?.success?.(this.$t('exports_v3.messages.export_success'))
                this.fetchHistory() // Refresh history
            } catch (error) {
                // Period crossing the cut-off date: show the popup, no download.
                if (await this.handlePeriodNotAvailable(error)) {
                    return
                }
                const payload = await this.parseErrorPayload(error)
                this.$toast.error((payload && payload.message) || this.$t('exports_v3.messages.export_error'))
            } finally {
                this.isGeneratingComptable = false
            }
        },

        async handleOperationnelExport() {
            if (!this.isTicketX && (!this.operationnel.startDate || !this.operationnel.endDate)) {
                this.$toast?.error?.(this.$t('exports_v3.messages.select_dates_error'))
                return
            }

            this.isGeneratingOperationnel = true

            try {
                if (this.operationnel.reportType === 'reservations') {
                    const payload = {
                        start_date: this.operationnel.startDate,
                        end_date: this.operationnel.endDate,
                        user_id: this.userId,
                    }

                    const response = await axios.post('/api/v3/export/reservations', payload, {
                        responseType: 'blob',
                        headers: this.authHeaders(),
                    })

                    const fileName = `Reservations_${this.operationnel.startDate}_to_${this.operationnel.endDate}.xlsx`
                    this.downloadFile(response.data, fileName)

                    this.$toast?.success?.(this.$t('exports_v3.messages.operationnel_success'))
                    this.fetchHistory()
                } else if (this.operationnel.reportType === 'mes_commandes') {
                    const payload = {
                        start_date: this.operationnel.startDate,
                        end_date: this.operationnel.endDate,
                        user_id: this.userId,
                    }

                    const response = await axios.post('/api/v3/export/orders', payload, {
                        responseType: 'blob',
                        headers: this.authHeaders(),
                    })

                    const fileName = `Mes_commandes_${this.operationnel.startDate}_to_${this.operationnel.endDate}.xlsx`
                    this.downloadFile(response.data, fileName)

                    this.$toast?.success?.(this.$t('exports_v3.messages.operationnel_success'))
                    this.fetchHistory()
                } else {
                    const isExcel = this.operationnel.reportType.endsWith('_excel')
                    const type = this.operationnel.reportType.includes('encaissements') ? 'collections' : 'sales'

                    const payload = {
                        start_date: null, // Let backend handle defaults
                        end_date: null, // Let backend handle defaults
                        format: isExcel ? 'xlsx' : 'pdf',
                        type: type,
                        user_id: this.userId,
                    }

                    const response = await axios.post('/api/v3/export/ticket-x', payload, {
                        responseType: 'blob',
                        headers: this.authHeaders(),
                    })

                    const extension = isExcel ? 'xlsx' : 'pdf'
                    const fileName = `Ticket_X_${type}_${new Date().toISOString().split('T')[0]}.${extension}`
                    this.downloadFile(response.data, fileName)

                    this.$toast?.success?.(this.$t('exports_v3.messages.operationnel_success'))
                    this.fetchHistory() // Refresh history
                }
            } catch (error) {
                // Period crossing the cut-off date: show the popup, no download.
                if (await this.handlePeriodNotAvailable(error)) {
                    return
                }
                const payload = await this.parseErrorPayload(error)
                this.$toast.error((payload && payload.message) || this.$t('exports_v3.messages.operationnel_error'))
            } finally {
                this.isGeneratingOperationnel = false
            }
        },

        getEndpoint() {
            if (this.comptable.documentType.startsWith('ticket_z')) {
                return '/api/v3/export/ticket-z'
            } else if (this.comptable.documentType === 'fec') {
                return '/api/v3/export/fec'
            } else if (this.comptable.documentType === 'factures_pdf') {
                return '/api/v3/export/invoices'
            }
            return ''
        },

        getPayload() {
            const payload = {
                start_date: this.comptable.startDate,
                end_date: this.comptable.endDate,
                user_id: this.userId,
            }

            if (this.comptable.documentType.startsWith('ticket_z')) {
                payload.format = this.comptable.documentType === 'ticket_z_pdf' ? 'pdf' : 'xlsx'
            } else if (this.comptable.documentType === 'factures_pdf') {
                payload.format = 'pdf'
            }

            return payload
        },

        getFileName() {
            const start = this.comptable.startDate
            const end = this.comptable.endDate
            const timestamp = new Date().getTime()

            if (this.comptable.documentType === 'ticket_z_pdf') {
                return `Ticket_Z_${start}_to_${end}_${timestamp}.pdf`
            } else if (this.comptable.documentType === 'ticket_z_excel') {
                return `Ticket_Z_${start}_to_${end}_${timestamp}.xlsx`
            } else if (this.comptable.documentType === 'fec') {
                return `FEC_${start}_to_${end}_${timestamp}.txt`
            } else if (this.comptable.documentType === 'factures_pdf') {
                return `Factures_${start}_to_${end}_${timestamp}.zip`
            }
            return 'export.pdf'
        },

        downloadFile(blob, fileName) {
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', fileName)
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)
        },

        handlePageChange(page) {
            this.fetchHistory(page)
        },
        handlePerPageChange(perPage) {
            this.perPage = Number(perPage)
            setPerPage('exports_items_per_page', this.perPage)
            this.fetchHistory(1)
        },

        async downloadExport(item) {
            try {
                const response = await axios.get(`/api/v3/export/download/${item.id}`, {
                    responseType: 'blob',
                    headers: this.authHeaders(),
                })
                this.downloadFile(response.data, item.name)
            } catch (error) {
                this.$toast.error(error.response.data.message)
            }
        },
    },
    mounted() {
        this.fetchHistory()
    },
}
</script>

<style scoped lang="scss">
/* Custom radio button styling to match brand purple */
input[type='radio'] {
    accent-color: #7d2dbd;
}

/* Ensure the main container has the correct background for V3 */
:deep(.content) {
    background-color: transparent !important;
}
</style>
